import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrash, 
  faSync, 
  faFile, 
  faGlobe,
  faExclamationTriangle,
  faCheck,
  faSpinner,
  faPlus,
  faUpload,
  faSitemap
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useAuthToken } from '../../contexts/AuthTokenContext';
import './IndexedContent.css';
import { isAdminUser } from '../../config/adminConfig';
import { Config } from '../../types';

export interface IndexedContentProps {
  config: Config;
  userId: string;
  filterType?: string; // 'document', 'web', or undefined for all
}

interface IndexedItem {
  id: string;
  type: string;
  url?: string;
  title: string;
  indexed_at: string;
  status: string;
  chunks?: number;
  isAdminContent?: boolean;
  userId?: string;
  metadata?: {
    contentType?: string;
    lastModified?: string;
    size?: number;
    error?: string;
    totalUrls?: number;
    filteredUrls?: number;
  };
}

interface AddWebsiteDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { type: 'website' | 'sitemap', url: string, isAdminContent: boolean }) => Promise<void>;
  submitting: boolean;
  error: string | null;
  success: string | null;
}

const AddWebsiteDialog: React.FC<AddWebsiteDialogProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  submitting,
  error,
  success
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<'website' | 'sitemap'>('website');
  const [url, setUrl] = useState('');
  const [restrictDomain, setRestrictDomain] = useState<boolean>(true);
  const { account } = useAuthToken();
  const isAdmin = isAdminUser(account?.username || '');

  const handleSubmit = async () => {
    if (!url) {
      return;
    }

    try {
      new URL(url);
    } catch (error) {
      return;
    }

    await onSubmit({ 
      type, 
      url,
      isAdminContent: isAdmin // Always set to true for admin users, false for regular users
    });
    if (!error) {
      setUrl('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('settings.addWebsite')}</DialogTitle>
      <DialogContent>
        <RadioGroup 
          value={type} 
          onChange={(e) => setType(e.target.value as 'website' | 'sitemap')}
          sx={{ mb: 2 }}
        >
          <FormControlLabel 
            value="website" 
            control={<Radio />} 
            label={t('settings.singleWebsite')} 
          />
          <FormControlLabel 
            value="sitemap" 
            control={<Radio />} 
            label={t('settings.sitemap')} 
          />
        </RadioGroup>
        
        <TextField
          fullWidth
          label={type === 'website' ? t('settings.websiteUrl') : t('settings.sitemapUrl')}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={type === 'website' ? "https://www.example.com" : "https://www.example.com/sitemap.xml"}
          helperText={type === 'website' ? t('settings.websiteUrlHelp') : t('settings.sitemapUrlHelp')}
          sx={{ mb: 2 }}
        />

        {type === 'sitemap' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={restrictDomain}
                onChange={(e) => setRestrictDomain(e.target.checked)}
              />
            }
            label={t('settings.restrictDomain')}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !url.trim()}
        >
          {submitting ? <CircularProgress size={24} /> : t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const IndexedContent: React.FC<IndexedContentProps> = ({ config, userId, filterType }) => {
  const { t } = useTranslation();
  const { getToken, account } = useAuthToken();
  const [indexedItems, setIndexedItems] = useState<IndexedItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isAdmin = isAdminUser(account?.username || '');

  const fetchIndexedContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getToken();
      
      // Build the URL with query parameters
      let url = `${config.apiUrl}/api/indexed-content`;
      const params = new URLSearchParams();
      
      // For website filter, don't send any type filter since we want both 'web' and 'webpage' types
      // and the backend doesn't support multiple types in one query
      if (filterType && filterType !== 'web') {
        params.append('type', filterType);
      }
      
      // Always include userId to filter content by user
      if (userId) {
        params.append('userId', userId);
      }
      
      // Add params to URL if any exist
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log('Fetching indexed content from:', url);
      console.log('Using userId:', userId);
      console.log('Using filterType:', filterType);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch indexed content: ${response.statusText}`);
      }
      
      let data = await response.json();
      
      // If we're filtering for web content, filter the results client-side to include both 'web' and 'webpage' types
      if (filterType === 'web') {
        data = data.filter((item: IndexedItem) => item.type === 'web' || item.type === 'webpage');
      }
      
      console.log('Indexed content data:', data);
      
      // Add more detailed logging
      if (data.length === 0) {
        console.log('No indexed content found');
        console.log('Active filter:', filterType);
      } else {
        console.log(`Found ${data.length} indexed content items`);
        
        // Count items by type
        const typeCount = data.reduce((acc: Record<string, number>, item: IndexedItem) => {
          acc[item.type] = (acc[item.type] || 0) + 1;
          return acc;
        }, {});
        console.log('Items by type:', typeCount);
        
        // Count items by status
        const statusCount = data.reduce((acc: Record<string, number>, item: IndexedItem) => {
          acc[item.status] = (acc[item.status] || 0) + 1;
          return acc;
        }, {});
        console.log('Items by status:', statusCount);
        
        // Log the first few items of each type for inspection
        const documentItems = data.filter((item: IndexedItem) => item.type === 'document').slice(0, 3);
        console.log('Sample document items:', documentItems);
        
        const webItems = data.filter((item: IndexedItem) => item.type === 'web' || item.type === 'webpage').slice(0, 3);
        console.log('Sample web items:', webItems);
      }
      
      setIndexedItems(data);
    } catch (err) {
      console.error('Error fetching indexed content:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [config.apiUrl, getToken, userId, filterType]);

  useEffect(() => {
    fetchIndexedContent();
  }, [fetchIndexedContent]);

  const handleOpenDialog = () => {
    setSubmitError(null);
    setSubmitSuccess(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmitWebsite = async ({ type, url, isAdminContent }: { type: 'website' | 'sitemap', url: string, isAdminContent: boolean }) => {
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const token = await getToken();
      
      console.log('Submitting website/sitemap:', { type, url, isAdminContent });
      const response = await fetch(`${config.apiUrl}/api/websites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type,
          url,
          userId,
          isAdminContent
        })
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to submit ${type}`);
        } catch (jsonError) {
          throw new Error(`Failed to submit ${type}: ${response.status} ${response.statusText}`);
        }
      }
      
      const result = await response.json();
      console.log('Submission result:', result);
      
      // Always refresh the list after submission, regardless of whether it was newly indexed
      await fetchIndexedContent();
      
      // Show success message with appropriate count
      const count = result.processedUrls || 1;
      if (type === 'website') {
        setSubmitSuccess(t('settings.websiteSubmitted'));
      } else {
        setSubmitSuccess(t('settings.sitemapSubmitted', { count }));
      }
    } catch (err) {
      console.error(`${type} submission error:`, err);
      setSubmitError(err instanceof Error ? err.message : `Failed to submit ${type}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteItem = async (id: string, type: string) => {
    if (!window.confirm(t('settings.confirmRemove'))) {
      return;
    }
    
    try {
      const token = await getToken();
      
      const response = await fetch(`${config.apiUrl}/api/indexed-content/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete content');
      }
      
      // Remove the item from the list
      setIndexedItems(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const handleReindexItem = async (url: string) => {
    try {
      const token = await getToken();
      
      // URL needs to be encoded for the API endpoint
      const encodedUrl = encodeURIComponent(url);
      
      const response = await fetch(`${config.apiUrl}/api/reindex/${encodedUrl}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reindex content');
      }
      
      // Refresh the list
      fetchIndexedContent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setError(null);

    try {
      const token = await getToken();
      const successfulUploads = [];
      const failedUploads = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('userId', userId);
          formData.append('isAdminContent', String(isAdmin)); // Always set to true for admin users

          const response = await fetch(`${config.apiUrl}/api/documents`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `Failed to upload ${file.name}`);
          }

          const result = await response.json();
          console.log(`Upload result for ${file.name}:`, result);
          successfulUploads.push(file.name);
        } catch (uploadError) {
          console.error(`Error uploading ${file.name}:`, uploadError);
          failedUploads.push({ name: file.name, error: uploadError });
        }
      }

      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Show success/failure message
      if (failedUploads.length === 0) {
        setSubmitSuccess(`Successfully uploaded ${successfulUploads.length} file(s).`);
      } else if (successfulUploads.length === 0) {
        setSubmitError(`Failed to upload ${failedUploads.length} file(s).`);
      } else {
        setSubmitSuccess(`Uploaded ${successfulUploads.length} file(s) successfully, but ${failedUploads.length} file(s) failed.`);
      }

      // Refresh the list after a short delay
      setTimeout(() => {
        fetchIndexedContent();
      }, 2000);
    } catch (err) {
      console.error('File upload error:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'indexed':
      case 'success':
        return <FontAwesomeIcon icon={faCheck} className="status-icon success" />;
      case 'pending':
      case 'processing':
        return <FontAwesomeIcon icon={faSpinner} className="status-icon pending" spin />;
      case 'failed':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="status-icon error" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'indexed':
      case 'success':
        return t('settings.indexed');
      case 'pending':
        return t('settings.pending');
      case 'processing':
        return t('settings.processing');
      case 'failed':
        return t('settings.failed');
      default:
        // If there's no direct mapping, try using the status key directly
        const translationKey = `settings.${status}`;
        const translated = t(translationKey);
        
        // If the translation is the same as the key, it means no translation was found
        return translated === translationKey ? status : translated;
    }
  };

  const getTypeIcon = (type: string, contentType?: string) => {
    if (type === 'document') {
      return <FontAwesomeIcon icon={faFile} className="type-icon document" />;
    } else if (type === 'web' || type === 'webpage') {
      return <FontAwesomeIcon icon={faGlobe} className="type-icon web" />;
    } else if (type === 'sitemap') {
      return <FontAwesomeIcon icon={faSitemap} className="type-icon sitemap" />;
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const getTitle = () => {
    if (filterType === 'document') {
      return t('settings.documents');
    } else if (filterType === 'web') {
      return t('settings.websites');
    } else {
      return t('settings.allDocuments');
    }
  };

  return (
    <div className="indexed-content-container">
      <div className="indexed-content-header">
        <Typography variant="h6" component="h2">
          {getTitle()}
        </Typography>
        
        <div className="indexed-content-actions">
          <Button 
            variant="contained" 
            startIcon={<FontAwesomeIcon icon={isUploading ? faSpinner : faUpload} spin={isUploading} />}
            onClick={handleFileUploadClick}
            disabled={isUploading}
          >
            {isUploading ? t('chat.uploading') : t('chat.uploadDocument')}
          </Button>
          <Button 
            variant="contained" 
            startIcon={<FontAwesomeIcon icon={faGlobe} />}
            onClick={handleOpenDialog}
          >
            {t('settings.addWebsite')}
          </Button>
        </div>
      </div>
      
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        multiple
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
      />
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {submitSuccess}
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : indexedItems.length === 0 ? (
        <Alert severity="info">
          {t('settings.noContentFound')}
        </Alert>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: 'calc(100vh - 280px)', 
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell width="10%">{t('settings.type')}</TableCell>
                <TableCell width="20%">{t('settings.sourcetitle')}</TableCell>
                <TableCell width="25%">{t('settings.source')}</TableCell>
                <TableCell width="10%">{t('settings.owner')}</TableCell>
                <TableCell width="15%">{t('settings.indexedAt')}</TableCell>
                <TableCell width="10%">{t('settings.status')}</TableCell>
                <TableCell width="10%">{t('settings.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {indexedItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {getTypeIcon(item.type, item.metadata?.contentType)}
                    <span className="type-text">
                      {item.type === 'document' ? t('settings.document') : 
                       item.type === 'web' || item.type === 'webpage' ? t('settings.webpage') : 
                       item.type === 'sitemap' ? t('settings.sitemap') : 
                       item.type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={item.title}>
                      <span className="truncate-text">{item.title}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {item.url ? (
                      <Tooltip title={item.url}>
                        <span className="truncate-text">
                          {item.url}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`owner-text ${item.isAdminContent ? 'admin-content' : 'personal-content'}`}>
                      {item.isAdminContent ? t('settings.availableForAll') : t('settings.personalDocument')}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(item.indexed_at)}</TableCell>
                  <TableCell>
                    <div className="status-container">
                      {getStatusIcon(item.status)}
                      <span className="status-text">
                        {getStatusText(item.status)}
                      </span>
                      {item.chunks && (
                        <span className="chunks-count">
                          ({item.chunks} {t('settings.chunks')})
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="actions-container">
                      {item.url && (
                        <Tooltip title={t('settings.reindex')}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleReindexItem(item.url!)}
                            disabled={item.status === 'pending' || item.status === 'processing'}
                          >
                            <FontAwesomeIcon icon={faSync} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={t('settings.remove')}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteItem(item.id, item.type)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Add Website Dialog */}
      <AddWebsiteDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitWebsite}
        submitting={submitting}
        error={submitError}
        success={submitSuccess}
      />
    </div>
  );
};

export default IndexedContent; 